import { TbGift } from "react-icons/tb";
import { Idea } from "@/api/types.ts";
import { useEffect, useState } from "react";
import { IdeaModal } from "@/components/Idea/IdeaModal.tsx";
import { useOnScreenAnalytics } from "@/helpers/onScreen.tsx";
import { datadogLogs } from "@datadog/browser-logs";
import {Badge, Caption, Text} from "@telegram-apps/telegram-ui";

import "./styles.css";

interface IdeaCardProps {
  idea: Idea;
  layout?: 'horizontal-scroll' | 'grid';
}

export const IdeaCard = ({ idea, layout = 'horizontal-scroll' }: IdeaCardProps) => {
  const [openedIdea, setOpenedIdea] = useState<Idea | null>(null);
  const [ref, isVisible] = useOnScreenAnalytics({ threshold: 0.5 }) as [React.RefObject<HTMLDivElement>, boolean];

  useEffect(() => {
    if (isVisible) {
      console.log('Idea is visible', idea.id);
      datadogLogs.logger.info('Idea is visible', { idea_id: idea.id });
    }
  }, [isVisible, idea.id]);

  const onOpenIdea = () => {
    datadogLogs.logger.info('Idea is opened', { idea_id: idea.id });
    setOpenedIdea(idea);
  };

  const onCloseIdea = () => {
    setOpenedIdea(null);
  };

  // Get middle size image (same logic as IdeaCell)
  let middleSizeImage = idea.image?.sizes[0];
  if (idea.image && idea.image?.sizes.length > 1) {
    middleSizeImage = idea.image?.sizes[1];
  }

  // Determine card width based on layout
  // For horizontal scroll: show 2 full cards + 20% of third card
  const cardWidthClass = layout === 'horizontal-scroll' 
    ? 'idea-card-horizontal-scroll flex-shrink-0' 
    : 'w-full';

  return (
    <>
      <div
        ref={ref}
        onClick={onOpenIdea}
        className={`idea-card ${cardWidthClass} flex flex-col bg-[var(--tgui--secondary_bg_color)] rounded-xl overflow-hidden cursor-pointer touch-target`}
      >
        {/* Image container with aspect ratio */}
        <div className="idea-image-container relative w-full aspect-[4/3] flex items-center justify-center">
          {middleSizeImage ? (
            <img
              src={middleSizeImage.link}
              alt={idea.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="idea-fallback-icon">
              <TbGift color="var(--tgui--hint_color)" size={48} />
            </div>
          )}
          
          {/* Price badge overlaid on image */}
          {idea.priceText && (<Badge className={"absolute top-2 right-2 "} large={false}
                                     type={"number"} mode={"critical"}>
            {idea.priceText}
          </Badge>)}
        </div>

        {/* Content container */}
        <div className="p-3 pb-5 flex flex-col gap-2 bg-section">
          {/* Title - truncated to 2 lines */}
          <Text weight={"2"}>
            {idea.title}
          </Text>

          {/* Description - truncated to 2-3 lines */}
          {idea.description && (
            <Caption className={"line-clamp-3"}>
              {idea.description}
            </Caption>
          )}
        </div>
      </div>

      {openedIdea && <IdeaModal idea={openedIdea} onClose={onCloseIdea} />}
    </>
  );
};
